import { Component, forwardRef, OnDestroy, OnInit } from '@angular/core';
import { ControlValueAccessor, FormArray, FormBuilder, FormGroup, NG_VALUE_ACCESSOR, AbstractControl } from '@angular/forms';
import { SweetAlertHelper } from 'app/core/helpers/sweet-alert-helper.service';
import { DualListBoxI, IDualListBoxOption, IDualListBoxEntry } from 'app/core/interfaces/dual-list-box.interface';
import { DualListBoxService } from '../../dual-list-box.service';
import { Subscription } from 'rxjs/Subscription';
import { isArray } from 'util';

@Component({
  selector: 'app-dual-list-box',
  templateUrl: './dual-list-box.component.html',
  styleUrls: ['./dual-list-box.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DualListBoxComponent),
      multi: true
    }
  ]
})
export class DualListBoxComponent implements OnInit, OnDestroy, ControlValueAccessor {

  disabledItemsList: number[] = null;
  form: FormGroup;
  private arraySubscriptions: Subscription[] = [];

  onChange: any = (value: any) => {};
  onTouch: any = () => {};

  get rightSelectedOptionFormArray(): FormArray {
    return this.form.get('rightSelectedOption') as FormArray;
  }

  get leftSelectedOptionFormArray(): FormArray {
    return this.form.get('leftSelectedOption') as FormArray;
  }

  get leftSelectedCtrl(): AbstractControl {
    return this.form.get('leftSelected');
  }

  get rightSelectedCtrl(): AbstractControl {
    return this.form.get('rightSelected');
  }

  get sendDataCtrl(): AbstractControl {
    return this.form.get('sendData');
  }


  constructor(
    private fb: FormBuilder,
    private sweetAlertHelper: SweetAlertHelper,
    private dualListBoxService: DualListBoxService,
  ) {
    this.form = this.fbGroup();
    this.subscriptions();
  }

  ngOnInit(): void {
  }

  private fbGroup(): FormGroup {
    return this.fb.group({
      sendData: false,
      rightSelected: undefined,
      rightSelectedOption: this.fb.array([]),
      leftSelected: undefined,
      leftSelectedOption: this.fb.array([]),
      disabledItemRight: null
    });
  }

  writeValue(value: DualListBoxI): void {
    if (value !== null && value !== undefined) {
      if (typeof value === 'object' && !isArray(value)) {
        this.resetLeftSelectedOptionFormArray();
        this.resetRightSelectedOptionFormArray();
        if (value.providerList && value.excludeProviderList) {
          if (value.providerList.length) {
            value.providerList.forEach( (providerRow: IDualListBoxEntry) => {
              this.rightSelectedOptionFormArray.insert(0, this.fb.group({
                datasetvaluefield: providerRow.id,
                datasetdisplayfields: providerRow.description
              }));
            });
          }
          if (value.excludeProviderList.length) {
            value.excludeProviderList.forEach((excludeRow: IDualListBoxEntry) => {
              this.leftSelectedOptionFormArray.insert(0, this.fb.group({
                datasetvaluefield: excludeRow.id,
                datasetdisplayfields: excludeRow.description
              }));
            });
          }
        }
      }

      if (value.disabledItemList && value.disabledItemList.length) {
        this.disabledItemsList = value.disabledItemList;
      } else {
        this.disabledItemsList = null;
      }

      if (value.preSelectedLeftIds && value.preSelectedLeftIds.length) {
        this.leftSelectedCtrl.patchValue(value.preSelectedLeftIds);
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  resetRightSelectedOptionFormArray(): void {
    while (this.rightSelectedOptionFormArray.length !== 0) {
      this.rightSelectedOptionFormArray.removeAt(0);
    }
  }

  resetLeftSelectedOptionFormArray(): void {
    while (this.leftSelectedOptionFormArray.length !== 0) {
      this.leftSelectedOptionFormArray.removeAt(0);
    }
  }

  addRightSelectedOptionFbGroupArray(data: IDualListBoxOption[]): void {
    if (data != null && data.length) {
      data.forEach((y: IDualListBoxOption) => {
        this.rightSelectedOptionFormArray.insert(0, this.fb.group({
          datasetvaluefield: y.datasetvaluefield,
          datasetdisplayfields: y.datasetdisplayfields
        }));
      });
    }
  }

  addLeftSelectedOptionFbGroupArray(data: IDualListBoxOption[]): void {
    if (data != null && data.length) {
      data.forEach((y: IDualListBoxOption) => {
        this.leftSelectedOptionFormArray.insert(0, this.fb.group({
            datasetvaluefield: y.datasetvaluefield,
            datasetdisplayfields: y.datasetdisplayfields
          })
        );
      });
    }
  }

  undoRedoRightAll(): void {
    if (this.rightSelectedOptionFormArray.value) {
      const data = [];
      let listRight = [];

      if (this.disabledItemsList && this.disabledItemsList.length) {
        listRight = this.rightSelectedOptionFormArray.value.filter((f: IDualListBoxOption) => {
          return !this.disabledItemsList.includes(f.datasetvaluefield);
        });

      } else {
        listRight = this.rightSelectedOptionFormArray.value;
      }

      listRight.forEach((y: IDualListBoxOption) => {
        data.push(this.rightSelectedOptionFormArray.value.find((f: IDualListBoxOption) => {
          return this.getOptionValue(f) === this.getOptionValue(y);
        }));

        this.rightSelectedOptionFormArray.removeAt(
            this.rightSelectedOptionFormArray.value.findIndex((f: IDualListBoxOption) => {
              return this.getOptionValue(f) === this.getOptionValue(y);
            })
        );
      });

      this.addLeftSelectedOptionFbGroupArray(data);
      this.form.patchValue({rightSelected: undefined});
    }
  }

  undoRedoRightSelected(): void {
    const selectedIds: number[] = this.rightSelectedCtrl.value || [];
    if (selectedIds && selectedIds.length) {
      const data: IDualListBoxOption[] = [];
      selectedIds.forEach((id: number) => {
        data.push(
          this.rightSelectedOptionFormArray.value.find((f: IDualListBoxOption) => {
            return this.getOptionValue(f) === id;
          })
        );

        this.rightSelectedOptionFormArray.removeAt(
          this.rightSelectedOptionFormArray.value.findIndex((f: IDualListBoxOption) => {
            return this.getOptionValue(f) === id;
          })
        );
      });
      this.addLeftSelectedOptionFbGroupArray(data);
      this.form.patchValue({rightSelected: undefined});
    }
  }

  undoRedoLeftSelected(): void {
    const selectedIds: number[] = this.leftSelectedCtrl.value || [];
    if (selectedIds && selectedIds.length) {
      const data: IDualListBoxOption[] = [];
      selectedIds.forEach((id: number) => {
        data.push(
          this.leftSelectedOptionFormArray.value.find((f: IDualListBoxOption) => {
           return this.getOptionValue(f) === id;
          })
        );
        this.leftSelectedOptionFormArray.removeAt(
          this.leftSelectedOptionFormArray.value.findIndex((f: IDualListBoxOption) => {
            return this.getOptionValue(f) === id;
          })
        );
      });
      this.addRightSelectedOptionFbGroupArray(data);
      this.form.patchValue({leftSelected: undefined});
    }
  }

  undoRedoLeftAll(): void {
    if (this.leftSelectedOptionFormArray.value) {
      const data: IDualListBoxOption[] = [];
      this.leftSelectedOptionFormArray.value.forEach((y: IDualListBoxOption) => {
        data.push(this.leftSelectedOptionFormArray.value.find((f: IDualListBoxOption) => {
          return this.getOptionValue(f) === this.getOptionValue(y);
        }));

        this.leftSelectedOptionFormArray.removeAt(
            this.leftSelectedOptionFormArray.value.findIndex((f: IDualListBoxOption) => {
              return this.getOptionValue(f) === this.getOptionValue(y);
            })
        );
      });
      this.addRightSelectedOptionFbGroupArray(data);
      this.form.patchValue({leftSelected: undefined});
    }
  }

  save(): void {
    this.sweetAlertHelper.createCustomAlert({
      title: this.dualListBoxService.getConfirmMessage(),
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: `Yes!`,
      cancelButtonText: 'No.'
    }).then((result) => {
      if (result.value) {
        this.sendDataCtrl.patchValue(true);
      }
    });
  }

  clean(): void {
    this.leftSelectedCtrl.patchValue(undefined);
    this.rightSelectedCtrl.patchValue(undefined);
  }

  private subscriptions(): void {
    this.arraySubscriptions.push(
      this.sendDataCtrl.valueChanges.subscribe((val) => {
        if (val) {
          this.onChange({ sendData: val,
            leftList: this.leftSelectedOptionFormArray.value,
            rightList: this.rightSelectedOptionFormArray.value,
            disabledItemsList: this.disabledItemsList
          });
        }
      })
    );

    this.arraySubscriptions.push(
      this.leftSelectedCtrl.valueChanges.subscribe((val: number[]) => {
        this.dualListBoxService.emitLeftSelectionChanged(val && val.length ? val : []);
      }),
    );
  }

  ngOnDestroy(): void {
    if (!!this.arraySubscriptions) {
      this.arraySubscriptions.forEach(value => value.unsubscribe());
    }
  }

  getOptionValue(item: IDualListBoxOption): number {
    return item.datasetvaluefield;
  }

  getOptionDisplay(item: IDualListBoxOption): string {
    return item ? item.datasetdisplayfields || '' : '';
  }

  isOptionDisabled(item: IDualListBoxOption): boolean {
    if (!item || !this.disabledItemsList) { return false; }
    return this.disabledItemsList.includes(item.datasetvaluefield);
  }
}



// WEBPACK FOOTER //
// ./src/app/utils/dual-list-box/components/dual-list-box/dual-list-box.component.ts